/**
 * useAutocompleteSuggestions Hook
 *
 * Generates search autocomplete options from tracked products and alerts.
 * Memoized to avoid recalculation on alert actions (optimistic updates).
 */

import { useMemo, type ReactNode } from 'react';
import { Icon, Text } from '@shopify/polaris';
import { PackageIcon } from '@shopify/polaris-icons';
import type { TrackedProduct, EnhancedAlert } from '../../types/dashboard';
import type { AutocompleteOption } from '../../types/search';

/**
 * Input parameters for useAutocompleteSuggestions
 */
export interface UseAutocompleteSuggestionsInput {
  /** Tracked products for product name suggestions */
  trackedProducts: TrackedProduct[];
  /** Alerts for SKU suggestions (use initial, not optimistic) */
  alerts: EnhancedAlert[];
}

/**
 * Return type for useAutocompleteSuggestions hook
 */
export interface UseAutocompleteSuggestionsReturn {
  /** Combined product and SKU suggestions for autocomplete */
  suggestions: AutocompleteOption[];
}

/**
 * Hook for generating search autocomplete suggestions
 *
 * Splits suggestions into stable parts to avoid recalculation on alert actions:
 * - Product suggestions only depend on trackedProducts (stable between loader calls)
 * - SKU suggestions use initial alerts (from loader), not optimistic state
 *
 * @param input - Tracked products and alerts data
 * @returns Combined autocomplete suggestions
 */
export function useAutocompleteSuggestions({
  trackedProducts,
  alerts,
}: UseAutocompleteSuggestionsInput): UseAutocompleteSuggestionsReturn {
  // Product suggestions only depend on trackedProducts (stable between loader calls)
  const productSuggestions = useMemo(() => {
    const suggestions: AutocompleteOption[] = [];
    const seenProducts = new Set<string>();

    trackedProducts.forEach((product) => {
      if (!seenProducts.has(product.product_id)) {
        seenProducts.add(product.product_id);
        suggestions.push({
          label: product.product_title,
          value: product.product_title,
          prefix: <Icon source={PackageIcon} tone="subdued" /> as ReactNode,
          category: 'product',
        });
      }
    });

    return suggestions;
  }, [trackedProducts]);

  // SKU suggestions use initial alerts (from loader), not optimistic state
  // This avoids recalculation when users dismiss/resolve alerts
  const skuSuggestions = useMemo(() => {
    const suggestions: AutocompleteOption[] = [];
    const seenSkus = new Set<string>();

    alerts.forEach((alert) => {
      if (alert.variant_sku && !seenSkus.has(alert.variant_sku)) {
        seenSkus.add(alert.variant_sku);
        suggestions.push({
          label: `SKU: ${alert.variant_sku}`,
          value: alert.variant_sku,
          prefix: (
            <Text as="span" tone="subdued" variant="bodySm">
              SKU
            </Text>
          ) as ReactNode,
          category: 'sku',
        });
      }
    });

    return suggestions;
  }, [alerts]);

  // Combined suggestions - only recalculates when either stable memo changes
  const suggestions = useMemo(
    () => [...productSuggestions, ...skuSuggestions],
    [productSuggestions, skuSuggestions]
  );

  return { suggestions };
}
